import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  ShoppingCart, Truck, Archive,
} from "lucide-react";

/**
 * Stage 3: Per-tenant Contacts Hub — customers + suppliers grid with
 * LTV-lite stats (outstanding + overdue across AR + AP). Driven by the
 * `GET /api/integrations/xero/tenants/{tenantId}/contacts` endpoint
 * which returns Xero's inline Balances block.
 */
export default function XeroContactsPage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ contacts: [], totals: null });
  const [loading, setLoading] = useState(false);

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/xero/tenants");
      setTenant((data || []).find((t) => t.tenant_id === tenantId)
        || { tenant_id: tenantId, tenant_name: tenantId });
    } catch (e) { logger.debug("XeroContacts: tenant lookup skipped", e); }
  }, [tenantId]);
  useEffect(() => { loadTenant(); }, [loadTenant]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ kind, page: String(page) });
      if (search.trim()) params.set("search", search.trim());
      if (includeArchived) params.set("include_archived", "true");
      const { data } = await api.get(
        `/integrations/xero/tenants/${tenantId}/contacts?${params}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load contacts");
    } finally { setLoading(false); }
  }, [tenantId, kind, page, search, includeArchived]);
  useEffect(() => { load(); }, [load]);

  const switchKind = (k) => { setKind(k); setPage(1); };
  const totals = data.totals || {};

  return (
    <div className="p-6 max-w-7xl" data-testid="xero-contacts-page">
      <Link to="/integrations/xero"
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-contacts-back">
        <ArrowLeft className="h-3 w-3" /> Back to Xero
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <Users className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1">
          <div className="label-overline">XERO · CONTACTS HUB</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {tenant?.tenant_name || "…"}
          </h1>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm"
          onClick={load} data-testid="xero-contacts-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* ── Kind tabs ── */}
      <div className="flex gap-2 mb-4">
        <KindTab active={kind === "all"} onClick={() => switchKind("all")}
          icon={<Users className="h-4 w-4" />} label="All"
          testid="xero-contacts-tab-all" />
        <KindTab active={kind === "customers"} onClick={() => switchKind("customers")}
          icon={<ShoppingCart className="h-4 w-4" />} label="Customers"
          testid="xero-contacts-tab-customers" />
        <KindTab active={kind === "suppliers"} onClick={() => switchKind("suppliers")}
          icon={<Truck className="h-4 w-4" />} label="Suppliers"
          testid="xero-contacts-tab-suppliers" />
      </div>

      {/* ── Totals pills (current page · current filter) ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <SummaryPill label="On page" value={totals.count ?? 0} />
        <SummaryPill label="Customers (page)" value={totals.customers ?? 0} />
        <SummaryPill label="Suppliers (page)" value={totals.suppliers ?? 0} />
        <SummaryPill label="AR outstanding"
          value={fmtMoney(totals.ar_outstanding)} accent="text-amber-700" />
        <SummaryPill label="AR overdue"
          value={fmtMoney(totals.ar_overdue)}
          accent={(totals.ar_overdue ?? 0) > 0 ? "text-red-700" : ""} />
        <SummaryPill label="AP outstanding"
          value={fmtMoney(totals.ap_outstanding)} accent="text-zinc-700" />
      </div>

      {/* ── Filters + pagination ── */}
      <Card className="rounded-sm border-zinc-200 mb-4">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex-1 min-w-[220px]">
            Search by name
            <Input type="search" value={search}
              placeholder="e.g. Acme Pty Ltd"
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="mt-1 h-9 text-xs"
              data-testid="xero-contacts-search" />
          </label>
          <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 inline-flex items-center gap-2">
            <input type="checkbox" checked={includeArchived}
              onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }}
              className="h-3.5 w-3.5"
              data-testid="xero-contacts-archived" />
            <span className="inline-flex items-center gap-1">
              <Archive className="h-3 w-3" /> Include archived
            </span>
          </label>
          <Button size="sm" variant="ghost" className="rounded-sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="xero-contacts-prev">
            <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
          </Button>
          <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">Page {page}</Badge>
          <Button size="sm" variant="ghost" className="rounded-sm"
            disabled={loading || (data.contacts || []).length < 100}
            onClick={() => setPage((p) => p + 1)}
            data-testid="xero-contacts-next">
            Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-0">
          <div className="grid grid-cols-[1.4fr_1.4fr_110px_110px_110px_110px_80px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
            <span>Name</span>
            <span>Email · Phone</span>
            <span className="text-right">AR outstanding</span>
            <span className="text-right">AR overdue</span>
            <span className="text-right">AP outstanding</span>
            <span className="text-right">AP overdue</span>
            <span className="text-right">Type</span>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : (data.contacts || []).length === 0 ? (
            <div className="p-8 text-center text-sm italic text-zinc-500"
              data-testid="xero-contacts-empty">
              No contacts match these filters.
            </div>
          ) : (
            (data.contacts || []).map((c) => (
              <div key={c.contact_id}
                className="grid grid-cols-[1.4fr_1.4fr_110px_110px_110px_110px_80px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center hover:bg-zinc-50"
                data-testid={`xero-contact-${c.contact_id}`}>
                <span className="truncate">
                  <span className="font-semibold">{c.name || "—"}</span>
                  {c.status === "ARCHIVED" && (
                    <Badge variant="outline"
                      className="rounded-sm text-[9px] ml-2 bg-zinc-100 text-zinc-500 border-zinc-200">
                      ARCHIVED
                    </Badge>
                  )}
                </span>
                <span className="truncate text-xs text-zinc-600">
                  {c.email || <span className="italic text-zinc-400">no email</span>}
                  {c.phone && <span className="font-mono text-[10px] ml-2 text-zinc-500">{c.phone}</span>}
                </span>
                <span className={`font-mono text-right ${
                  Number(c.ar_outstanding) > 0 ? "text-amber-700 font-semibold" : "text-zinc-400"
                }`}>
                  {fmtMoney(c.ar_outstanding)}
                </span>
                <span className={`font-mono text-right ${
                  Number(c.ar_overdue) > 0 ? "text-red-700 font-semibold" : "text-zinc-400"
                }`}>
                  {fmtMoney(c.ar_overdue)}
                </span>
                <span className={`font-mono text-right ${
                  Number(c.ap_outstanding) > 0 ? "text-amber-700 font-semibold" : "text-zinc-400"
                }`}>
                  {fmtMoney(c.ap_outstanding)}
                </span>
                <span className={`font-mono text-right ${
                  Number(c.ap_overdue) > 0 ? "text-red-700 font-semibold" : "text-zinc-400"
                }`}>
                  {fmtMoney(c.ap_overdue)}
                </span>
                <span className="text-right space-x-1">
                  {c.is_customer && (
                    <Badge variant="outline"
                      className="rounded-sm text-[9px] bg-emerald-50 text-emerald-800 border-emerald-200">
                      C
                    </Badge>
                  )}
                  {c.is_supplier && (
                    <Badge variant="outline"
                      className="rounded-sm text-[9px] bg-blue-50 text-blue-800 border-blue-200">
                      S
                    </Badge>
                  )}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KindTab({ active, onClick, icon, label, testid }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-4 h-10 rounded-sm border font-display font-bold text-sm inline-flex items-center gap-2 transition-colors ${
        active ? "bg-zinc-900 text-white border-zinc-900"
               : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
      }`}
      data-testid={testid}>
      {icon} {label}
    </button>
  );
}

function SummaryPill({ label, value, accent }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white">
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-xl mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
